import React, { useState, useRef, useMemo } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  X, ChevronRight, ChevronLeft, Upload, FileSpreadsheet, Loader2,
  CheckCircle, AlertTriangle, Brain, Trash2, Plus, Sparkles, Edit3,
} from "lucide-react";

const PROVIDERS = [
  { id: "MOBA",       label: "MOBA" },
  { id: "Distromel",  label: "Distromel" },
  { id: "Movolytics", label: "Movolytics" },
  { id: "Movisat",    label: "Movisat" },
  { id: "OPTIMA",     label: "Optima" },
  { id: "Indra",      label: "Indra" },
  { id: "Bitpayt",    label: "Bitpayt" },
  { id: "Sigma Rocas", label: "Sigma Rocas" },
  { id: "",           label: "Otro / autodetectar" },
];

const CONTRACT_TYPES = ["SER", "ZV", "GRU", "CCTV", "OTRO"];

const fmtEur = (n) =>
  `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const STEPS = ["Subir Excel", "Revisar partidas", "Confirmar y crear"];

export default function OfferWizard({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [providerHint, setProviderHint] = useState("MOBA");
  const [municipalityHint, setMunicipalityHint] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractErr, setExtractErr] = useState("");
  const [extracted, setExtracted] = useState(null); // {title, description, proveedor, ..., cost_sections}
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const fileInputRef = useRef(null);

  // Metadata editable in step 2/3 — initialized from `extracted`
  const [meta, setMeta] = useState({
    title: "",
    description: "",
    municipality: "",
    deadline: "",
    contract_type: "SER",
    responsible_business: "",
    responsible_business_email: "",
    linked_licitacion_id: "",
  });

  const reset = () => {
    setStep(0);
    setFile(null);
    setProviderHint("MOBA");
    setMunicipalityHint("");
    setExtracted(null);
    setExtractErr("");
    setCreateErr("");
    setMeta({
      title: "", description: "", municipality: "", deadline: "",
      contract_type: "SER", responsible_business: "", responsible_business_email: "",
      linked_licitacion_id: "",
    });
  };

  const close = () => { reset(); onClose?.(); };

  // ── Step 1 → Step 2: send file to AI ────────────────────────────────────
  const extract = async () => {
    if (!file) { setExtractErr("Selecciona un archivo .xlsx o .xlsm"); return; }
    setExtracting(true);
    setExtractErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("provider_hint", providerHint || "");
      fd.append("municipality_hint", municipalityHint || "");
      const { data } = await axios.post(
        `${API}/offers/wizard/extract-excel`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 },
      );
      setExtracted(data);
      setMeta(m => ({
        ...m,
        title: data.title || m.title,
        description: data.description || m.description,
        municipality: data.municipality || municipalityHint || m.municipality,
      }));
      setStep(1);
    } catch (e) {
      setExtractErr(e.response?.data?.detail || e.message || "Error procesando el Excel");
    } finally {
      setExtracting(false);
    }
  };

  // ── Editing sections in step 2 ──────────────────────────────────────────
  const updateItem = (sIdx, iIdx, field, value) => {
    setExtracted(prev => {
      const next = structuredClone(prev);
      const it = next.cost_sections[sIdx].items[iIdx];
      it[field] = field === "concepto" || field === "nota" ? value : Number(value || 0);
      return next;
    });
  };
  const updateSection = (sIdx, field, value) => {
    setExtracted(prev => {
      const next = structuredClone(prev);
      next.cost_sections[sIdx][field] = value;
      return next;
    });
  };
  const addItem = (sIdx) => {
    setExtracted(prev => {
      const next = structuredClone(prev);
      next.cost_sections[sIdx].items.push({
        id: crypto.randomUUID(),
        concepto: "", cantidad: 1, precio_unitario: 0, nota: "",
      });
      return next;
    });
  };
  const removeItem = (sIdx, iIdx) => {
    setExtracted(prev => {
      const next = structuredClone(prev);
      next.cost_sections[sIdx].items.splice(iIdx, 1);
      return next;
    });
  };
  const removeSection = (sIdx) => {
    if (!window.confirm("¿Eliminar esta sección?")) return;
    setExtracted(prev => {
      const next = structuredClone(prev);
      next.cost_sections.splice(sIdx, 1);
      return next;
    });
  };

  // Totals re-calculated on the fly from edited sections
  const liveTotals = useMemo(() => {
    if (!extracted?.cost_sections) return { capex: 0, opex: 0 };
    let capex = 0, opex = 0;
    for (const s of extracted.cost_sections) {
      const years = s.years || 1;
      const t = (s.items || []).reduce(
        (acc, it) => acc + (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) * years, 0
      );
      if (String(s.category || "").toUpperCase() === "CAPEX") capex += t;
      else opex += t;
    }
    return { capex, opex };
  }, [extracted]);

  // ── Step 3 → create offer ───────────────────────────────────────────────
  const create = async () => {
    if (!meta.title) { setCreateErr("El título es obligatorio"); return; }
    if (!window.confirm("⚠ Vas a CREAR una nueva oferta TIC a partir de los datos extraídos por IA. ¿Continuar?")) return;
    setCreating(true);
    setCreateErr("");
    try {
      const payload = {
        title: meta.title,
        licitacion: meta.linked_licitacion_id ? `Licitación ${meta.linked_licitacion_id}` : "",
        contract_type: meta.contract_type || "SER",
        municipality: meta.municipality || extracted.municipality || "",
        deadline: meta.deadline || null,
        responsible_business: meta.responsible_business || "",
        responsible_business_email: meta.responsible_business_email || null,
        responsible_tech: "David Redondo",
        description: meta.description || extracted.description || "",
        cost_sections: extracted.cost_sections.map(s => ({
          id: s.id || crypto.randomUUID(),
          category: String(s.category || "").toUpperCase(),
          subcategory: s.subcategory || "",
          section_name: s.section_name || "",
          years: s.years || 1,
          items: (s.items || []).map(it => ({
            id: it.id || crypto.randomUUID(),
            concepto: it.concepto || "",
            cantidad: Number(it.cantidad) || 0,
            precio_unitario: Number(it.precio_unitario) || 0,
            nota: it.nota || "",
          })),
        })),
      };
      const { data } = await axios.post(`${API}/offers`, payload);
      onCreated?.(data);
      close();
      navigate(`/offers/${data.id}`);
    } catch (e) {
      setCreateErr(e.response?.data?.detail || e.message || "Error creando la oferta");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="offer-wizard">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl my-8 flex flex-col" style={{ maxHeight: "92vh" }}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
              <Sparkles size={18} className="text-[#007934]" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 font-['Outfit']">Nueva oferta TIC desde Excel</h2>
              <p className="text-xs text-gray-500">Wizard con Pando — soporta MOBA, Distromel, Movolytics, Movisat, etc.</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="wizard-close">
            <X size={18} />
          </button>
        </div>

        {/* ── Step indicator ────────────────────────────────────────────── */}
        <div className="px-5 pt-4 pb-2 flex items-center gap-2">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div
                className={`flex items-center gap-2 ${i === step ? "text-[#007934]" : "text-gray-400"}`}
                data-testid={`wizard-step-${i}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  i < step ? "bg-[#007934] text-white" :
                  i === step ? "bg-[#007934] text-white" : "bg-gray-100"
                }`}>
                  {i < step ? <CheckCircle size={14} /> : i + 1}
                </span>
                <span className="text-xs font-semibold">{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-[#007934]" : "bg-gray-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Body (scrollable) ─────────────────────────────────────────── */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {step === 0 && (
            <Step1Upload
              file={file} setFile={setFile}
              providerHint={providerHint} setProviderHint={setProviderHint}
              municipalityHint={municipalityHint} setMunicipalityHint={setMunicipalityHint}
              extracting={extracting} extractErr={extractErr}
              fileInputRef={fileInputRef}
            />
          )}

          {step === 1 && extracted && (
            <Step2Review
              extracted={extracted}
              liveTotals={liveTotals}
              updateSection={updateSection}
              updateItem={updateItem}
              addItem={addItem}
              removeItem={removeItem}
              removeSection={removeSection}
            />
          )}

          {step === 2 && extracted && (
            <Step3Confirm
              extracted={extracted}
              liveTotals={liveTotals}
              meta={meta}
              setMeta={setMeta}
              createErr={createErr}
            />
          )}
        </div>

        {/* ── Footer / Nav ──────────────────────────────────────────────── */}
        <div className="p-4 border-t border-gray-100 flex items-center gap-2 flex-shrink-0 bg-gray-50/50">
          {step > 0 && (
            <button
              onClick={() => setStep(s => s - 1)}
              disabled={extracting || creating}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2"
              data-testid="wizard-back">
              <ChevronLeft size={14}/> Atrás
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {step === 0 && (
              <button
                onClick={extract}
                disabled={!file || extracting}
                className="bg-[#007934] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2"
                data-testid="wizard-extract-btn">
                {extracting
                  ? <><Loader2 size={14} className="animate-spin"/> Analizando con Pando…</>
                  : <><Brain size={14}/> Analizar Excel con Pando</>}
              </button>
            )}
            {step === 1 && (
              <button
                onClick={() => setStep(2)}
                className="bg-[#007934] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#006528] flex items-center gap-2"
                data-testid="wizard-next-btn">
                Continuar <ChevronRight size={14}/>
              </button>
            )}
            {step === 2 && (
              <button
                onClick={create}
                disabled={creating || !meta.title}
                className="bg-[#007934] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2"
                data-testid="wizard-create-btn">
                {creating
                  ? <><Loader2 size={14} className="animate-spin"/> Creando…</>
                  : <><CheckCircle size={14}/> Crear oferta</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STEP 1 · Upload Excel + provider hint
// ════════════════════════════════════════════════════════════════════════════
function Step1Upload({ file, setFile, providerHint, setProviderHint, municipalityHint, setMunicipalityHint, extracting, extractErr, fileInputRef }) {
  return (
    <div className="space-y-4" data-testid="wizard-step-1-body">
      <p className="text-sm text-gray-600">
        Sube el Excel del proveedor con el detalle de inversión y mantenimiento. La Pando detectará automáticamente
        las partidas <span className="font-semibold text-[#007934]">CAPEX</span> y <span className="font-semibold text-amber-700">OPEX</span>,
        agrupándolas en secciones que podrás revisar y editar antes de crear la oferta.
      </p>

      {/* File drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          file ? "border-[#007934] bg-[#E8F5E9]" : "border-gray-200 hover:border-[#007934] hover:bg-gray-50"
        }`}
        data-testid="wizard-drop-zone">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={e => setFile(e.target.files?.[0] || null)} />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-[#007934]">
            <FileSpreadsheet size={20} />
            <span className="text-sm font-semibold">{file.name}</span>
            <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
            <button onClick={e => { e.stopPropagation(); setFile(null); }}
              className="ml-2 text-gray-400 hover:text-red-500">
              <X size={14}/>
            </button>
          </div>
        ) : (
          <>
            <Upload size={28} className="mx-auto mb-2 text-gray-300"/>
            <p className="text-sm font-medium text-gray-600">Haz clic o arrastra el Excel</p>
            <p className="text-xs text-gray-400 mt-1">Presupuesto del proveedor (.xlsx, .xlsm — máx. 25 MB)</p>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
            Proveedor del SI
          </label>
          <select
            value={providerHint}
            onChange={e => setProviderHint(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
            data-testid="wizard-provider-select">
            {PROVIDERS.map(p => (
              <option key={p.id || "other"} value={p.id}>{p.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-gray-400 mt-1">La Pando confirmará / sobreescribirá si encuentra otro proveedor en el Excel.</p>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
            Municipio / Contrato (opcional)
          </label>
          <input
            value={municipalityHint}
            onChange={e => setMunicipalityHint(e.target.value)}
            placeholder="ej. El Escorial (Madrid)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
            data-testid="wizard-municipality-hint" />
        </div>
      </div>

      {extractErr && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-800 flex items-start gap-2">
          <AlertTriangle size={14} className="text-rose-600 flex-shrink-0 mt-0.5"/>
          <span>{extractErr}</span>
        </div>
      )}

      {extracting && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          <div className="flex items-center gap-2 mb-1 font-semibold">
            <Loader2 size={14} className="animate-spin"/> Procesando con Claude Sonnet 4.6…
          </div>
          <p>Puede tardar 20–60s según el tamaño del Excel. La Pando está leyendo todas las hojas, identificando CAPEX/OPEX y agrupando por categorías.</p>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STEP 2 · Review extracted sections
// ════════════════════════════════════════════════════════════════════════════
function Step2Review({ extracted, liveTotals, updateSection, updateItem, addItem, removeItem, removeSection }) {
  return (
    <div className="space-y-4" data-testid="wizard-step-2-body">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Proveedor"   value={extracted.proveedor || "—"} />
        <Kpi label="CAPEX total" value={fmtEur(liveTotals.capex)} color="text-[#007934]"
             sub={extracted.capex_total_eur && Math.abs(liveTotals.capex - extracted.capex_total_eur) > 1
               ? `Excel: ${fmtEur(extracted.capex_total_eur)}` : null}/>
        <Kpi label="OPEX / año"  value={fmtEur(liveTotals.opex)}  color="text-amber-700"
             sub={extracted.opex_total_eur && Math.abs(liveTotals.opex - extracted.opex_total_eur) > 1
               ? `Excel: ${fmtEur(extracted.opex_total_eur)}` : null}/>
        <Kpi label="Secciones"   value={extracted.cost_sections.length} />
      </div>

      {/* Warnings */}
      {(extracted.warnings || []).length > 0 && (
        <details className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="wizard-warnings">
          <summary className="text-xs font-bold text-amber-900 cursor-pointer flex items-center gap-1">
            <AlertTriangle size={12}/> {extracted.warnings.length} observaciones de la Pando
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] text-amber-900 list-disc list-inside">
            {extracted.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </details>
      )}

      <p className="text-xs text-gray-500 flex items-center gap-1">
        <Edit3 size={11}/> Edita cualquier celda. Los totales se recalculan en directo.
      </p>

      {/* Sections */}
      <div className="space-y-3">
        {extracted.cost_sections.map((s, sIdx) => {
          const isCapex = String(s.category || "").toUpperCase() === "CAPEX";
          const sectionTotal = (s.items || []).reduce(
            (a, it) => a + (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) * (s.years || 1), 0);
          return (
            <div key={s.id || sIdx} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`wizard-section-${sIdx}`}>
              {/* Section header */}
              <div className={`flex items-center justify-between gap-2 px-3 py-2 ${isCapex ? "bg-emerald-50" : "bg-amber-50"}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    isCapex ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"
                  }`}>{s.category}</span>
                  <input
                    value={s.section_name || ""}
                    onChange={e => updateSection(sIdx, "section_name", e.target.value)}
                    className="text-sm font-semibold bg-transparent border-0 focus:outline-none flex-1 min-w-0"
                    placeholder="Nombre de sección"/>
                  <input
                    value={s.subcategory || ""}
                    onChange={e => updateSection(sIdx, "subcategory", e.target.value)}
                    className="text-[10px] text-gray-600 bg-white/60 rounded px-2 py-0.5 border border-gray-200 w-32"
                    placeholder="Subcategoría"/>
                </div>
                <span className="text-xs font-mono font-bold text-gray-700">{fmtEur(sectionTotal)}</span>
                <button
                  onClick={() => removeSection(sIdx)}
                  className="text-gray-400 hover:text-red-500"
                  data-testid={`wizard-remove-section-${sIdx}`}>
                  <Trash2 size={13}/>
                </button>
              </div>
              {/* Items */}
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 text-[10px] uppercase">
                    <th className="text-left px-2 py-1 font-semibold">Concepto</th>
                    <th className="text-right px-2 py-1 font-semibold w-16">Cant.</th>
                    <th className="text-right px-2 py-1 font-semibold w-24">€/ud</th>
                    <th className="text-right px-2 py-1 font-semibold w-24">Total</th>
                    <th className="w-6"/>
                  </tr>
                </thead>
                <tbody>
                  {(s.items || []).map((it, iIdx) => {
                    const total = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) * (s.years || 1);
                    return (
                      <tr key={it.id || iIdx} className="border-t border-gray-100">
                        <td className="px-2 py-1">
                          <input
                            value={it.concepto || ""}
                            onChange={e => updateItem(sIdx, iIdx, "concepto", e.target.value)}
                            className="w-full bg-transparent border-0 text-xs focus:outline-none focus:bg-yellow-50"/>
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number" step="any"
                            value={it.cantidad ?? 0}
                            onChange={e => updateItem(sIdx, iIdx, "cantidad", e.target.value)}
                            className="w-full text-right tabular-nums bg-transparent border-0 text-xs focus:outline-none focus:bg-yellow-50"/>
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number" step="any"
                            value={it.precio_unitario ?? 0}
                            onChange={e => updateItem(sIdx, iIdx, "precio_unitario", e.target.value)}
                            className="w-full text-right tabular-nums bg-transparent border-0 text-xs focus:outline-none focus:bg-yellow-50"/>
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums font-mono font-semibold">
                          {fmtEur(total)}
                        </td>
                        <td className="px-1">
                          <button
                            onClick={() => removeItem(sIdx, iIdx)}
                            className="text-gray-300 hover:text-red-500">
                            <Trash2 size={11}/>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button
                onClick={() => addItem(sIdx)}
                className="w-full px-2 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-1"
                data-testid={`wizard-add-item-${sIdx}`}>
                <Plus size={11}/> Añadir línea
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STEP 3 · Confirm + metadata + create
// ════════════════════════════════════════════════════════════════════════════
function Step3Confirm({ extracted, liveTotals, meta, setMeta, createErr }) {
  return (
    <div className="space-y-4" data-testid="wizard-step-3-body">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Proveedor"   value={extracted.proveedor || "—"} />
        <Kpi label="CAPEX total" value={fmtEur(liveTotals.capex)} color="text-[#007934]"/>
        <Kpi label="OPEX / año"  value={fmtEur(liveTotals.opex)}  color="text-amber-700"/>
        <Kpi label="Total Oferta" value={fmtEur(liveTotals.capex + liveTotals.opex)} color="text-gray-900"/>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
            Título de la oferta *
          </label>
          <input
            value={meta.title}
            onChange={e => setMeta(m => ({ ...m, title: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
            data-testid="wizard-meta-title"/>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Municipio</label>
            <input
              value={meta.municipality}
              onChange={e => setMeta(m => ({ ...m, municipality: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              data-testid="wizard-meta-muni"/>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Tipo contrato</label>
            <select
              value={meta.contract_type}
              onChange={e => setMeta(m => ({ ...m, contract_type: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
              {CONTRACT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Deadline / cierre</label>
            <input
              type="date"
              value={meta.deadline}
              onChange={e => setMeta(m => ({ ...m, deadline: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              data-testid="wizard-meta-deadline"/>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Responsable negocio</label>
            <input
              value={meta.responsible_business}
              onChange={e => setMeta(m => ({ ...m, responsible_business: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"/>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
            Descripción (la Pando propone una, puedes editarla)
          </label>
          <textarea
            value={meta.description}
            rows={4}
            onChange={e => setMeta(m => ({ ...m, description: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
            data-testid="wizard-meta-description"/>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
          <Sparkles size={12} className="text-blue-600 flex-shrink-0 mt-0.5"/>
          <div>
            <p className="font-semibold mb-0.5">Al crear la oferta:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Quedará en estado <strong>Borrador</strong>.</li>
              <li>Podrás solicitar validación TIC y luego aprobación CFO desde la pantalla de detalle.</li>
              <li>La Pando detectó <strong>{extracted.cost_sections.length} secciones</strong> con un total de <strong>{fmtEur(liveTotals.capex + liveTotals.opex)}</strong>.</li>
            </ul>
          </div>
        </div>

        {createErr && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-800 flex items-start gap-2">
            <AlertTriangle size={14} className="text-rose-600 flex-shrink-0 mt-0.5"/>
            <span>{createErr}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, color = "text-gray-900", sub = null }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-2">
      <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide">{label}</div>
      <div className={`text-base font-bold mt-0.5 font-['Outfit'] truncate ${color}`}>{value}</div>
      {sub && <div className="text-[9px] text-amber-700 mt-0.5">{sub}</div>}
    </div>
  );
}
